import { isMixpanelDevMode } from "./mixpanelDevMode";

type Mixpanel = typeof import("mixpanel-browser").default;

const TOKEN = "4d616b20599f9d185171fde41c77cb1b";

let sdk: Mixpanel | null = null;
let unavailable = false;
let loading: Promise<void> | null = null;
const queued: Array<(mp: Mixpanel) => void> = [];

// Content blockers stop this chunk or swap in an empty module. Analytics is
// optional, so give up quietly instead of raising an unhandled rejection.
const giveUp = () => {
  unavailable = true;
  queued.length = 0;
};

const load = (): Promise<void> => {
  loading ??= import("mixpanel-browser").then(({ default: mp }) => {
    if (!mp) return giveUp();
    mp.init(TOKEN, {
      autocapture: false,
      api_host: "https://api-eu.mixpanel.com",
      // Capture clicks / rage clicks / dead clicks while a replay is recording,
      // to populate Heatmaps. These events are exempt from the plan's data
      // allowance. Recording itself is started per-session from the front page,
      // not sampled globally here — see events/sessionReplay.ts.
      record_heatmap_data: true,
      // Dev mode (local dev + staging): log every event to the console (see
      // events/mixpanelTrack.ts) AND stop sending so clicks never pollute the
      // production project. See isMixpanelDevMode for when this is on.
      debug: isMixpanelDevMode(),
    });
    if (isMixpanelDevMode()) mp.opt_out_tracking();

    sdk = mp;
    // FIFO, so identify() still lands before the track() calls queued after it.
    queued.splice(0).forEach((fn) => fn(mp));
  }, giveUp);
  return loading;
};

/**
 * Run something against the SDK, loading it on first use. The import is fired
 * off during startup but not awaited, so the ~1 MB of Mixpanel is fetched and
 * parsed alongside first paint rather than before it.
 */
export const withMixpanel = (fn: (mp: Mixpanel) => void): void => {
  if (sdk) {
    fn(sdk);
    return;
  }
  if (unavailable) return;
  queued.push(fn);
  void load();
};

/** Start loading without queueing work. Call once, after the app renders. */
export const preloadMixpanel = (): void => void load();

/**
 * The distinct_id, without waiting for the SDK. Requests fire before the import
 * resolves, and the id has to ride on them for backend events to stitch to the
 * same person — so read it out of the cookie the SDK persists it to. Returns
 * undefined only for a visitor the SDK has never run for, whose requests went
 * without the header before this was deferred too.
 */
export const getDistinctIdSync = (): string | undefined => {
  if (sdk) return sdk.get_distinct_id() as string | undefined;
  try {
    return distinctIdFromCookie(document.cookie);
  } catch {
    return undefined;
  }
};

/** Pure half of the above, so the parsing is testable without a DOM. */
export const distinctIdFromCookie = (
  cookieString: string,
): string | undefined => {
  const name = `mp_${TOKEN}_mixpanel=`;
  const raw = cookieString
    .split("; ")
    .find((c) => c.startsWith(name))
    ?.slice(name.length);
  if (!raw) return undefined;
  try {
    return JSON.parse(decodeURIComponent(raw)).distinct_id;
  } catch {
    return undefined;
  }
};
